<template>
  <div class="w-full mt-5">
    <!-- Show skeleton while loading -->
    <MagazineHomePaginatedContentSkeleton v-if="isLoading" />

    <!-- Show content when loaded -->
    <template v-else>
      <div class="flex flex-wrap justify-between gap-x-16 gap-y-5">
        <MagazineArticleCard
          v-for="article in articleClusterItems"
          :key="article.link"
          class="md:w-[calc(50%-32px)]"
          :author="article.author"
          :date="article.date"
          :image="article.image"
          :description="article.description"
          :link="article.link"
          :title="article.title"
        />
      </div>

      <!-- Show More button -->
      <div
        class="mt-20 text-center flex flex-col md:flex-row justify-center items-center gap-6"
      >
        <Button
          v-if="showMoreVisible"
          :disabled="isLoadingMore"
          color="primary"
          variant="outlined"
          class="min-w-[150px] px-3"
          @click="loadMore"
        >
          <span v-if="isLoadingMore">Loading...</span>
          <span v-else>{{ t('magazine.showMore') }}</span>
        </Button>

        <FilterPagination
          v-if="totalPages > 1"
          class="!m-0"
          :hits-per-page="articlesPerPage"
          :nb-hits="totalArticles"
          :nb-pages="totalPages || 1"
          :current-page="virtualCurrentPage"
          @updated="onPageChange"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { useRouteQuery } from '@vueuse/router'
import type { StrapiMagazineArticle } from '~/composables/types/strapi/strapi-magazine'

// No props needed - this component shows all articles

const $localePath = useLocalePath()
const localePath = $localePath
const { t, locale } = useI18n()
const { query } = useRoute()

// Pagination
const pageQuery = useRouteQuery('page', '1')
const currentPage = computed(() => Number(pageQuery.value) || 1)
const articlesPerPage = 10 // amount of articles per page

// Cache buster for navigation
const cacheBuster = ref(Date.now())

// Show More state
const isLoadingMore = ref(false)
const isShowMoreMode = ref(false) // Show More mode flag
const allLoadedArticles = ref<any[]>([]) // All loaded articles
const nextPageToLoad = ref(2) // Track which page to load next in Show More mode
const showMoreBasePage = ref(1) // Base page from which Show More started

// On component mount, reset Show More mode
onMounted(() => {
  resetShowMoreMode()
})

// Function to reset Show More mode
const resetShowMoreMode = () => {
  isShowMoreMode.value = false
  allLoadedArticles.value = []
  nextPageToLoad.value = currentPage.value + 1
  showMoreBasePage.value = currentPage.value
}

// Watch for page changes in URL to update nextPageToLoad
watch(currentPage, (newPage) => {
  if (!isShowMoreMode.value) {
    nextPageToLoad.value = newPage + 1
    showMoreBasePage.value = newPage
  }
})

// No category filtering needed - this component shows all articles

const createArticleClusterItem = (article: StrapiMagazineArticle) => ({
  author: articleGetters.getAuthor(article),
  date: formatDate(
    articleGetters.getCreatedAt(article) || '',
    locale.value,
    'dd. DD. MMM YYYY'
  ),
  image: articleGetters.getHeaderImageUrl(article, 'thumbnail' as any) || '',
  title: articleGetters.getTitle(article) || '',
  description: articleGetters.getDescription(article) || '',
  link: localePath(`/magazine/${articleGetters.getSlug(article) || ''}`),
})

// Reactive URL for request
const articlesUrl = computed(() => {
  const config = useRuntimeConfig()
  const { strapiApiBaseURL } = config.public
  const params = new URLSearchParams()
  params.set('locale', locale.value)
  params.set('pagination[page]', currentPage.value.toString())
  params.set('pagination[pageSize]', articlesPerPage.toString())
  params.set('populate[admin_user]', 'true')
  params.set('populate[header_image]', 'true')
  params.set('populate[categories]', 'true')
  params.set('sort', 'createdAt:desc')

  // No category filtering - show all articles

  return `${strapiApiBaseURL}articles?${params.toString()}`
})

// Use $fetch directly with reactivity
const { data: articlesResponse, pending: isLoading } = await useFetch(
  articlesUrl,
  {
    key: computed(
      () => `magazine-articles-${currentPage.value}-${cacheBuster.value}`
    ),
    headers: {
      Authorization: `Bearer ${useRuntimeConfig().public.strapiApiKey}`,
    },
    transform: (data: any) => data, // Keep full response structure
    watch: [articlesUrl], // Ensure reactivity to URL changes
  }
)

// Computed properties for pagination
const currentPageArticles = computed(
  () => (articlesResponse.value as any)?.data || []
)
const totalArticles = computed(
  () => (articlesResponse.value as any)?.meta?.pagination?.total || 0
)
const totalPages = computed(
  () => (articlesResponse.value as any)?.meta?.pagination?.pageCount || 1
)

// Combine all loaded articles
const articles = computed(() => {
  // If not in Show More mode, show only current page
  if (!isShowMoreMode.value) {
    return currentPageArticles.value
  }

  // In Show More mode, return all loaded articles
  return allLoadedArticles.value
})

// Calculate virtual current page based on loaded articles in Show More mode
const virtualCurrentPage = computed(() => {
  if (!isShowMoreMode.value) {
    return currentPage.value
  }

  // In Show More mode, use nextPageToLoad - 1 as the current loaded page
  // This is more accurate than calculating from article count
  return Math.min(nextPageToLoad.value - 1, totalPages.value)
})

// Show More logic (show only if there are more articles to load)
const showMoreVisible = computed(() => {
  if (!isShowMoreMode.value) {
    // In pagination mode, show if there are next pages
    return currentPage.value < totalPages.value
  } else {
    // In Show More mode, check if we have reached the last page
    // Calculate which page we would be loading next
    const currentVirtualPage = virtualCurrentPage.value
    const hasMorePages = currentVirtualPage < totalPages.value
    const hasMoreArticles = allLoadedArticles.value.length < totalArticles.value

    // Show button only if both conditions are true:
    // 1. We haven't reached the last page
    // 2. We haven't loaded all articles
    return hasMorePages && hasMoreArticles
  }
})

// All articles for the current page
const articleClusterItems = computed(() => {
  if (!articles.value) return []

  return articles.value.map((article: StrapiMagazineArticle) =>
    createArticleClusterItem(article)
  )
})

// Function to load additional content
const loadMore = async () => {
  if (isLoadingMore.value) return

  // Check if we already have all articles loaded or reached the last page
  if (
    isShowMoreMode.value &&
    (allLoadedArticles.value.length >= totalArticles.value ||
      virtualCurrentPage.value >= totalPages.value)
  ) {
    return
  }

  // Check if there are more pages to load
  if (nextPageToLoad.value > totalPages.value) {
    return
  }

  isLoadingMore.value = true
  isShowMoreMode.value = true // Enable Show More mode

  try {
    // Initialize allLoadedArticles with current page articles if first time
    if (allLoadedArticles.value.length === 0) {
      allLoadedArticles.value = [...currentPageArticles.value]
      showMoreBasePage.value = currentPage.value // Set base page for Show More mode
    }

    // Load next page data directly without changing URL
    const config = useRuntimeConfig()
    const { strapiApiBaseURL } = config.public
    const params = new URLSearchParams()
    params.set('locale', locale.value)
    params.set('pagination[page]', nextPageToLoad.value.toString())
    params.set('pagination[pageSize]', articlesPerPage.toString())
    params.set('populate[admin_user]', 'true')
    params.set('populate[header_image]', 'true')
    params.set('populate[categories]', 'true')
    params.set('sort', 'createdAt:desc')

    // No category filtering - show all articles

    const nextPageUrl = `${strapiApiBaseURL}articles?${params.toString()}`

    const response = (await $fetch(nextPageUrl, {
      headers: {
        Authorization: `Bearer ${useRuntimeConfig().public.strapiApiKey}`,
      },
    })) as any

    // Add new articles to the existing ones
    if (response?.data) {
      const existingIds = new Set(allLoadedArticles.value.map((a: any) => a.id))
      const newArticles = response.data.filter(
        (a: any) => !existingIds.has(a.id)
      )

      if (newArticles.length > 0) {
        allLoadedArticles.value = [...allLoadedArticles.value, ...newArticles]
      }

      // Always increment to next page, even if no new articles were added
      // This prevents infinite loading attempts
      nextPageToLoad.value += 1
    } else {
      // If no data received, increment to prevent infinite loading
      nextPageToLoad.value += 1
    }
  } finally {
    isLoadingMore.value = false
  }
}

// Page change handler
const onPageChange = async (page: number) => {
  // On direct navigation, disable Show More mode and reset articles
  resetShowMoreMode()
  nextPageToLoad.value = page + 1 // Reset for new page
  showMoreBasePage.value = page // Reset base page

  // Update cache buster to force fresh data fetch
  cacheBuster.value = Date.now()

  await navigateTo({
    query: {
      ...query,
      page: page.toString(),
    },
  })

  // Scroll to the beginning of the content on direct navigation
  nextTick(() => {
    document.getElementById('magazine')?.scrollIntoView({ behavior: 'smooth' })
  })
}
</script>
